import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import {
  User,
  CompanyReport,
  PositionType,
  ExternalCompany,
} from "@/CodegenTypes";
import type { FullReport } from "@/types/reportTypes";
import { first, sumBy, some } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import AccountSelectSearch from "@/components/modules/account/AccountSelectSearch.vue";
import PositionStatementsModal from "@/components/modules/report/PositionStatementsModal.vue";
import StrikeStatementsModal from "@/components/modules/report/StrikeStatementsModal.vue";
import OutsetStrikeStatementsModal from "@/components/modules/report/OutsetStrikeStatementsModal.vue";
import { negateNumber } from "@/helpers/negateNumber";
import UserBreadcrumb from "@/components/common/UserBreadcrumb.vue";
import TicketsModal from "@/components/modules/report/TicketsModal.vue";
import { canSeeDdCompanyLevel } from "@/helpers/user/canSeeDdCompanyLevel";
import { FEATURE_TOGGLES } from "@/config";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { useQuery } from "@vue/apollo-composable";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'CollectionReport',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const { $t } = useTranslation();


const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedEndReleaseDate = computed(() => {
  const query = route.query.endReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const routeUserId = computed(() => {
  const query = route.query.userId;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedDateRangeReady = computed(() =>
  !!selectedStartReleaseDate.value && !!selectedEndReleaseDate.value
);

const fullReportVariables = computed(() => ({
  releaseDateStart: selectedStartReleaseDate.value,
  releaseDateEnd: selectedEndReleaseDate.value,
  userId: routeUserId.value,
}));

const companyReportVariables = computed(() => ({
  releaseDateStart: selectedStartReleaseDate.value,
  releaseDateEnd: selectedEndReleaseDate.value,
}));

const { result: meResult, loading: meLoading } = useQuery<{ me: User }>(
  queries.me,
  {
    fetchPolicy: "cache-first",
  }
);

const me = computed(() => meResult.value?.me);

const seeCompanyLevel = computed(() => {
  return canSeeDdCompanyLevel(me.value);
});

const fullReportQuery = FEATURE_TOGGLES.useV1Legacy
  ? queries.fullReportLegacy
  : queries.fullReport;

const { result: fullReportResult, loading: fullReportLoading } = useQuery<{
  fullReport: FullReport;
}>(fullReportQuery, fullReportVariables, {
  enabled: computed(() => selectedDateRangeReady.value && !!routeUserId.value),
});

const fullReport = computed(() => fullReportResult.value?.fullReport);
// const fullReport = ref<UserReport>();
// watch(
//   () => fullReportResult.value?.fullReport,
//   (value) => {
//     if (value) {
//       fullReport.value = value;
//     }
//   }
// );

const companyReportQuery = FEATURE_TOGGLES.useV1Legacy
  ? queries.companyReportLegacy
  : queries.companyReport;

const { result: companyReportResult, loading: companyReportLoading } =
  useQuery<{
    companyReport: CompanyReport;
  }>(companyReportQuery, companyReportVariables, {
    enabled: computed(
      () => selectedDateRangeReady.value && seeCompanyLevel.value
    ),
  });

const showPositionModal = ref(false);
const showStrikeModal = ref(false);
const showOutsetStrikeModal = ref(false);
const selectedUserId = ref(null as null | User["userId"]);
const includeDownlines = ref(true as boolean);
const selectedExternalCompanyId = ref(null as null | ExternalCompany["userId"]);
const showTicketsModal = ref(false);
const showAccountSelectionModal = ref(false);

const companyReport = computed(() => companyReportResult.value?.companyReport);

const loading = computed(
  () => meLoading.value || fullReportLoading.value || companyReportLoading.value
);

const shareClaimTable = computed(() => {
  const rows = [];

  if (downlinesWithTickets.value) {
    rows.push(
      ...downlinesWithTickets.value.map((dl) => {
        const shareClaimStatements = dl.positionStatements?.filter(
          (s) => s.positionType === PositionType.Outset
        );
        const position = 0;
        const externalShareClaim = shareClaimStatements?.length
          ? sumBy(shareClaimStatements, (s) => s.uplinePosition)
          : 0;
        return {
          userId: dl.userId.toUpperCase(),
          username: "",
          position,
          externalShareClaim,
          total: position + externalShareClaim,
        };
      })
    );
  }

  if (companyReport.value?.outsetTickets) {
    rows.push(
      ...companyReport.value.outsetTickets.rows.map((outsetTicket) => {
        const position = negateNumber(outsetTicket.position);
        const externalShareClaim = outsetTicket.externalShareClaim || 0;
        return {
          userId: outsetTicket.name,
          username: "",
          position,
          externalShareClaim: -externalShareClaim,
          total: position + -externalShareClaim,
        };
      })
    );
  }

  return {
    summary: {
      position: sumBy(rows, (row) => row.position),
      externalShareClaim: sumBy(rows, (row) => row.externalShareClaim),
      total: sumBy(rows, (row) => row.total),
    },
    rows,
  };
});

const isNotAdminViewingAdmin = computed(() => {
  return route.query?.userId === "admin" && me.value?.userId !== "admin";
});

const selectedUserStatement = computed(() => {
  if (!fullReport.value) {
    return null;
  }
  return [fullReport.value.consolidated, ...fullReport.value.downlines].find(
    (s) => s.userId === selectedUserId.value
  );
});

const showCompany = computed(() => {
  return route.query.userId === "admin";
});

const netProfit = computed(() => {
  if (!fullReport.value || !downlinesSummary.value) {
    return 0;
  }
  const netProfit =
    fullReport.value.consolidated.netBal - downlinesSummary.value.netBal;
  if (Math.abs(netProfit) < 0.001) {
    return 0;
  }
  return netProfit;
});

const userTickets = computed(() => {
  if (!fullReport.value) {
    return null;
  }
  const user = fullReport.value.consolidated;
  const shareClaimPositionStatements = user.positionStatements?.filter(
    (s) => s.positionType === PositionType.Outset
  );
  if (shareClaimPositionStatements?.length) {
    const shareClaim = sumBy(
      shareClaimPositionStatements,
      (s) => s.uplinePosition
    );
    return {
      ...user,
      shareClaim,
      total: shareClaim + user.netBal,
    };
  } else {
    return {
      ...user,
      shareClaim: 0,
      total: user.netBal,
    };
  }
});

const downlinesWithTickets = computed(() => {
  const downlines = fullReport.value?.downlines || [];
  return downlines
    .filter((dl) =>
      some(
        Object.values(dl).filter((val) => typeof val === "number" && val !== 0)
      )
    )
    .map((dl) => {
      const shareClaimPositionStatements = dl.positionStatements?.filter(
        (s) => s.positionType === PositionType.Outset
      );
      if (shareClaimPositionStatements?.length) {
        const shareClaim = sumBy(
          shareClaimPositionStatements,
          (s) => s.uplinePosition
        );
        return {
          ...dl,
          shareClaim,
          total: shareClaim + dl.netBal,
        };
      } else {
        return {
          ...dl,
          shareClaim: 0,
          total: dl.netBal,
        };
      }
    });
});

const externalCompaniesWithTickets = computed(() => {
  return companyReport.value?.outsetTickets.rows.map((company) => {
    const shareClaim = (company.externalShareClaim || 0) * -1;
    return {
      ...company,
      userId: company.name,
      companyStake: 0,
      netBal: company.net,
      position: company.position,
      shareClaim,
      total: shareClaim + company.net,
    };
  });
});

const downlinesSummary = computed(() => {
  if (!userTickets.value) {
    return null;
  }
  if (
    userTickets.value.userId === "admin" &&
    externalCompaniesWithTickets.value
  ) {
    const combined = [
      ...downlinesWithTickets.value,
      ...externalCompaniesWithTickets.value,
    ];
    return {
      big: sumBy(combined, (dl) => dl.big),
      small: sumBy(combined, (dl) => dl.small),
      amount: sumBy(combined, (dl) => dl.amount),
      rebate: sumBy(combined, (dl) => dl.rebate),
      amountLessRebate: sumBy(combined, (dl) => dl.amountLessRebate),
      strikeAmount: sumBy(combined, (dl) => dl.strikeAmount),
      net: sumBy(combined, (dl) => dl.net),
      companyStake: sumBy(combined, (dl) => dl.companyStake),
      position: sumBy(combined, (dl) => dl.position),
      netBal: sumBy(combined, (dl) => dl.netBal),
      shareClaim: sumBy(combined, (dl) => dl.shareClaim),
      total: sumBy(combined, (dl) => dl.total),
    };
  } else {
    return {
      big:
        userTickets.value.big -
        sumBy(downlinesWithTickets.value, (dl) => dl.big),
      small:
        userTickets.value.small -
        sumBy(downlinesWithTickets.value, (dl) => dl.small),
      amount:
        userTickets.value.amount -
        sumBy(downlinesWithTickets.value, (dl) => dl.amount),
      rebate:
        userTickets.value.rebate -
        sumBy(downlinesWithTickets.value, (dl) => dl.rebate),
      amountLessRebate:
        userTickets.value.amountLessRebate -
        sumBy(downlinesWithTickets.value, (dl) => dl.amountLessRebate),
      strikeAmount:
        userTickets.value.strikeAmount -
        sumBy(downlinesWithTickets.value, (dl) => dl.strikeAmount),
      net:
        userTickets.value.net -
        sumBy(downlinesWithTickets.value, (dl) => dl.net),
      companyStake:
        userTickets.value.companyStake -
        sumBy(downlinesWithTickets.value, (dl) => dl.companyStake),
      position:
        userTickets.value.position -
        sumBy(downlinesWithTickets.value, (dl) => dl.position),
      netBal:
        userTickets.value.netBal -
        sumBy(downlinesWithTickets.value, (dl) => dl.netBal),
      shareClaim:
        userTickets.value.shareClaim -
        sumBy(downlinesWithTickets.value, (dl) => dl.shareClaim),
      total:
        userTickets.value.total -
        sumBy(downlinesWithTickets.value, (dl) => dl.total),
    };
  }
});

function showPosition(userId?: User["userId"]) {
  if (userId) {
    selectedUserId.value = userId;
  }
  showPositionModal.value = true;
}
function showTickets(
  userId: User["userId"] | undefined,
  includeDownlinesVar: boolean
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  includeDownlines.value = includeDownlinesVar;
  showTicketsModal.value = true;
}
function showStrike(userId?: User["userId"]) {
  if (userId) {
    selectedUserId.value = userId;
  }
  showStrikeModal.value = true;
}

function showOutsetStrike(userId?: ExternalCompany["userId"]) {
  if (userId) {
    selectedExternalCompanyId.value = userId;
  }
  showOutsetStrikeModal.value = true;
}

function seeAdmin() {
  router.push({
    name: "collection report",
    query: { ...route.query, userId: "admin" },
  });
}

return { __sfc: true,router, route, $t, selectedStartReleaseDate, selectedEndReleaseDate, routeUserId, selectedDateRangeReady, fullReportVariables, companyReportVariables, meResult, meLoading, me, seeCompanyLevel, fullReportQuery, fullReportResult, fullReportLoading, fullReport, companyReportQuery, companyReportResult, companyReportLoading, showPositionModal, showStrikeModal, showOutsetStrikeModal, selectedUserId, includeDownlines, selectedExternalCompanyId, showTicketsModal, showAccountSelectionModal, companyReport, loading, shareClaimTable, isNotAdminViewingAdmin, selectedUserStatement, showCompany, netProfit, userTickets, downlinesWithTickets, externalCompaniesWithTickets, downlinesSummary, showPosition, showTickets, showStrike, showOutsetStrike, seeAdmin, formatDollar, AccountSelectModal, AccountSelectSearch, PositionStatementsModal, StrikeStatementsModal, OutsetStrikeStatementsModal, UserBreadcrumb, TicketsModal, FEATURE_TOGGLES }
}

})